import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getInspectionSettings,
  inspection,
  getSelectedCustomDictionaryIDs,
  app,
  getInspectionOriginalOrgId,
} from 'store';
import { useUserStateStore } from 'zstore/userStateStore';
import { getOrganizationId } from 'utils/auth';
import { Dictionaries } from '@verify/shared-utils';
import { useCustomDictionaries } from 'store/queries/dictionaries/customDictionaries';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MultiSelect } from '@globalvisioninc/ui/multi-select';
import type { MultiSelectProps } from '@globalvisioninc/ui/multi-select';
import { applyDictionarySelection } from './dictionarySelection';

type DictionaryOptionGroup = Extract<MultiSelectProps['options'][number], { options: unknown }>;

const DictionarySelect = () => {
  const dispatch = useDispatch();
  const { dictionaryName } = useSelector(getInspectionSettings);
  const customDictionaryIDs = useSelector(getSelectedCustomDictionaryIDs);
  const originalOrgId = useSelector(getInspectionOriginalOrgId);
  const { user } = useUserStateStore();
  const userOrgId = getOrganizationId(user);
  const dictionaryList = Object.values(Dictionaries);
  const flags = useFlags();

  const isClonedFromDifferentOrg = useMemo(() => {
    if (!originalOrgId || !userOrgId) {
      return false;
    }
    return originalOrgId !== userOrgId;
  }, [originalOrgId, userOrgId]);

  const additionalDictionaryIds = isClonedFromDifferentOrg ? customDictionaryIDs : undefined;
  const { customDictionaryList } = useCustomDictionaries({ getAll: true, additionalDictionaryIds });

  const dictionaries: Dictionaries[] = useMemo(() => {
    if (!dictionaryName) {
      return [];
    }
    if (Array.isArray(dictionaryName)) {
      return dictionaryName;
    }
    return [dictionaryName];
  }, [dictionaryName]);

  const languageDictionaryValues = useMemo(() => new Set<string>(dictionaryList), [dictionaryList]);

  const selectedValues = useMemo(() => [...dictionaries, ...customDictionaryIDs], [dictionaries, customDictionaryIDs]);

  const isLastLanguageDictionary = dictionaries.length === 1;

  const options = useMemo(() => {
    const groups: DictionaryOptionGroup[] = [];

    if (customDictionaryList && customDictionaryList.length > 0) {
      groups.push({
        label: 'Custom Dictionaries',
        options: customDictionaryList.map((customDictionary) => ({
          label: customDictionary.name,
          value: customDictionary.id,
        })),
      });
    }

    groups.push({
      label: 'Dictionary',
      options: dictionaryList.map((dictionary) => ({
        label: dictionary,
        value: dictionary,
        isDisabled: isLastLanguageDictionary && dictionaries.includes(dictionary),
      })),
    });

    return groups;
  }, [customDictionaryList, dictionaryList, dictionaries, isLastLanguageDictionary]);

  if (!customDictionaryList || !dictionaryName) {
    return null;
  }

  const commitSelection = (nextValues: string[]) => {
    const resolvedValues = applyDictionarySelection(
      nextValues,
      selectedValues,
      languageDictionaryValues,
      Boolean(flags.enableMultiDictionarySelect),
    );

    const nextLanguage: Dictionaries[] = [];
    const nextCustom: string[] = [];
    for (const value of resolvedValues) {
      if (languageDictionaryValues.has(value)) {
        nextLanguage.push(value as Dictionaries);
      } else {
        nextCustom.push(value);
      }
    }

    const languageChanged =
      nextLanguage.length !== dictionaries.length ||
      nextLanguage.some((dictionary, index) => dictionary !== dictionaries[index]);
    const customChanged =
      nextCustom.length !== customDictionaryIDs.length ||
      nextCustom.some((id, index) => id !== customDictionaryIDs[index]);

    if (languageChanged) {
      dispatch(app.actions.updateUserPreferences({ userPreferenceName: 'lastDictionary', value: nextLanguage }));
      dispatch(inspection.actions.setInspectionSettings({ dictionaryName: nextLanguage }));
    }

    if (customChanged) {
      dispatch(inspection.actions.setSelectedCustomDictionaryIDs(nextCustom));
    }
  };

  return (
    <MultiSelect.Root
      options={options}
      value={selectedValues}
      onValueChange={commitSelection}
      label="Dictionary"
      hasSearch={false}
      maxCount={selectedValues.length || 1}
      avoidCollisions
      shouldRequireSelection
    >
      <MultiSelect.Trigger />
    </MultiSelect.Root>
  );
};

export default DictionarySelect;
